import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { Customer, Transaction, Person, CustomerType } from '../types';
import { customersAPI, transactionsAPI, personsAPI } from '../services/api';
import { useConfirm } from '../context/ConfirmContext';
import TransactionsTable from './TransactionsTable';
import Pagination from './Pagination';
import CustomerPersons from './CustomerPersons';
import { usePagination } from '../hooks/usePagination';
import { addRedirectToUrl } from '../utils/redirectUtils';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dateFormat';

const CustomerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [unpaidTransactions, setUnpaidTransactions] = useState<Transaction[]>([]);
  const [persons, setPersons] = useState<Person[]>([]);
  const [managingPersons, setManagingPersons] = useState(false);
  const [personManagementVersion, setPersonManagementVersion] = useState(0);
  const [deletingPersonId, setDeletingPersonId] = useState<number | null>(null);
  const [personsError, setPersonsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const confirm = useConfirm();

  const totalUnpaidAmount = useMemo(
    () => unpaidTransactions.reduce((sum, transaction) => sum + transaction.totalAmount, 0),
    [unpaidTransactions]
  );

  useEffect(() => {
    if (id) {
      loadCustomerDetails(parseInt(id));
    }
  }, [id, location.key]);

  const refreshContactPersons = async () => {
    if (!customer) return;
    try {
      const allPersons = await personsAPI.getPersons();
      setPersons(allPersons.filter(person =>
        person.customers?.some(cp => cp.customerId === customer.id && !cp.isFormer)
      ));
      setPersonsError(null);
    } catch (err) {
      setPersonsError('Failed to refresh contact persons. Reload the page to see the latest contacts.');
      console.error('Error refreshing contact persons:', err);
    }
  };

  const loadCustomerDetails = async (customerId: number) => {
    try {
      setLoading(true);
      
      // Load customer details, unpaid transactions, and persons in parallel
      const [customerData, unpaidData, allPersons] = await Promise.all([
        customersAPI.getCustomer(customerId),
        transactionsAPI.getUnpaidTransactions(customerId),
        personsAPI.getPersons()
      ]);

      setCustomer(customerData);
      setUnpaidTransactions(unpaidData);
      
      // Filter persons that belong to this customer (exclude former relationships)
      const customerPersons = allPersons.filter(person =>
        person.customers && person.customers.some(cp => cp.customerId === customerId && !cp.isFormer)
      );
      setPersons(customerPersons);
      
      setError(null);
    } catch (err) {
      setError('Failed to load customer details');
      console.error('Error loading customer details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customer || deleting) return;

    const isConfirmed = await confirm({
      title: 'Delete customer',
      message: `Are you sure you want to delete "${customer.title}"? This action cannot be undone and may also delete linked projects and related data.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger'
    });
    if (!isConfirmed) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await customersAPI.deleteCustomer(customer.id);
      navigate('/customers');
    } catch (err) {
      setDeleteError('Failed to delete customer');
      console.error('Error deleting customer:', err);
      setDeleting(false);
    }
  };

  const handleDeletePerson = async (person: Person) => {
    if (deletingPersonId !== null) return;
    setDeletingPersonId(person.id);
    setPersonsError(null);
    try {
      const isConfirmed = await confirm({
        title: 'Delete person',
        message: `Delete "${person.firstName} ${person.lastName}"? This deletes the person record across all customers. To remove only this customer's assignment, use Manage assignments.`,
        confirmText: 'Delete person',
        cancelText: 'Cancel',
        variant: 'danger'
      });
      if (!isConfirmed) return;

      await personsAPI.deletePerson(person.id);
      setPersons(current => current.filter(item => item.id !== person.id));
      setPersonManagementVersion(version => version + 1);
    } catch (err) {
      setPersonsError('Failed to delete person');
      console.error('Error deleting person:', err);
    } finally {
      setDeletingPersonId(null);
    }
  };

  const getContactTypeBadge = (contactType: number) => {
    switch (contactType) {
      case 0: // Hot
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#374151', color: '#fff' }}
          >
            Hot
          </span>
        );
      case 1: // Cold
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#6b7280', color: '#fff' }}
          >
            Cold
          </span>
        );
      default:
        return (
          <span
            className="badge contact-type-badge"
            style={{ backgroundColor: '#6b7280', color: '#fff' }}
          >
            Unknown
          </span>
        );
    }
  };

  const getTypeText = (type: CustomerType) => {
    switch (type) {
      case CustomerType.Government: return 'Government';
      case CustomerType.Education: return 'Education';
      case CustomerType.Legal: return 'Legal';
      case CustomerType.Healthcare: return 'Healthcare';
      case CustomerType.Retail: return 'Retail';
      case CustomerType.Banking: return 'Banking';
      case CustomerType.Nonprofit: return 'Nonprofit';
      case CustomerType.Publishers: return 'Publishers';
      case CustomerType.Agriculture: return 'Agriculture';
      case CustomerType.Logistics: return 'Logistics';
      case CustomerType.Telecommunications: return 'Telecommunications';
      case CustomerType.Media: return 'Media';
      case CustomerType.Hospitality: return 'Hospitality';
      default: return 'Unknown';
    }
  };

  const getTypeBadgeColor = (type: CustomerType) => {
    switch (type) {
      case CustomerType.Government:
        return '#6b7280';
      case CustomerType.Education:
        return '#a1a1aa';
      case CustomerType.Legal:
        return '#4b5563';
      case CustomerType.Healthcare:
        return '#374151';
      case CustomerType.Retail:
        return '#52525b';
      case CustomerType.Banking:
        return '#3f3f46';
      case CustomerType.Nonprofit:
        return '#4b5563';
      case CustomerType.Publishers:
        return '#1f2328';
      case CustomerType.Agriculture:
        return '#111111';
      case CustomerType.Logistics:
        return '#52525b';
      case CustomerType.Telecommunications:
        return '#6b7280';
      case CustomerType.Media:
        return '#a1a1aa';
      case CustomerType.Hospitality:
        return '#4b5563';
      default:
        return '#6b7280';
    }
  };


  const {
    currentPage,
    pageSize,
    paginatedData: paginatedTransactions,
    totalItems,
    goToPage,
    setPageSize
  } = usePagination({ data: unpaidTransactions, initialPageSize: 10 });

  const getProjectStatusTitle = (statusId: number): string => {
    switch (statusId) {
      case 1: return 'Request';
      case 2: return 'Canceled';
      case 3: return 'In Progress';
      case 4: return 'Completed';
      case 5: return 'Support';
      case 6: return 'Lead';
      case 7: return 'Opportunity';
      case 8: return 'Rejected';
      case 9: return 'Follow-up';
      case 10: return 'Confirmed';
      case 11: return 'Maintenance';
      case 12: return 'Lost';
      case 13: return 'Internal';
      default: return 'Unknown';
    }
  };

  const getProjectIncomeUsd = (projectTransactions: { typeId: number; totalAmount: number; isCompleted: boolean }[]) => {
    return projectTransactions
      .filter(t => t.isCompleted && t.typeId === 1 && t.totalAmount > 0)
      .reduce((acc, t) => acc + t.totalAmount, 0);
  };

  const getProjectExpenseUsd = (projectTransactions: { typeId: number; totalAmount: number; isCompleted: boolean }[]) => {
    const raw = projectTransactions
      .filter(t => t.isCompleted && (t.typeId === 2 || t.totalAmount < 0))
      .reduce((acc, t) => acc + (t.totalAmount < 0 ? Math.abs(t.totalAmount) : t.totalAmount), 0);
    return raw;
  };

  const sortedProjects = [...(customer?.projects ?? [])].sort((a, b) => {
    // Newest first
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const {
    currentPage: projectPage,
    pageSize: projectPageSize,
    paginatedData: paginatedProjects,
    totalItems: projectTotalItems,
    goToPage: goToProjectPage,
    setPageSize: setProjectPageSize
  } = usePagination({ data: sortedProjects, initialPageSize: 10 });

  const {
    currentPage: personPage,
    pageSize: personPageSize,
    paginatedData: paginatedPersons,
    totalItems: personTotalItems,
    goToPage: goToPersonPage,
    setPageSize: setPersonPageSize
  } = usePagination({ data: persons, initialPageSize: 6 });

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border" role="status" />
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          {error || 'Customer not found'}
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="container-fluid customer-details-page">
      {deleteError && (
        <div className="alert alert-danger" role="alert">
          {deleteError}
        </div>
      )}
      <div className="row mb-4 customer-details-header">
        <div className="col-md-6">
          <h2 className="customer-details-title">{customer.title}</h2>
          {customer.group && (
            <div className="text-muted mb-2">{customer.group}</div>
          )}
        </div>
        <div className="col-md-3">
          <div className="alert alert-warning mb-0 customer-unpaid-summary" role="status">
            <div className="small text-uppercase fw-semibold">Total unpaid transactions</div>
            <strong>{formatCurrency(totalUnpaidAmount, 'USD')}</strong>
          </div>
        </div>
        <div className="col-md-3 ms-auto d-flex justify-content-end">
          <div className="btn-group customer-details-actions justify-content-end w-100">
            <button
              className="btn btn-primary"
              onClick={() => navigate(addRedirectToUrl(`/customers/edit/${customer.id}`, location.pathname + location.search))}
            >
              Edit
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteCustomer}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => navigate(-1)}
            >
              Back
            </button>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          <div className="card customer-details-card">
            <div className="card-header">
              <h5 className="mb-0">Customer Information</h5>
            </div>
            <div className="card-body customer-info-compact">
              <div className="row">
                <div className="col-md-6">
                  <dl className="row">
                    <dt className="col-sm-4">Official ID:</dt>
                    <dd className="col-sm-8">{customer.officialId || '-'}</dd>

                    <dt className="col-sm-4">Country / Location:</dt>
                    <dd className="col-sm-8">
                      {customer.country || (customer.longitude && customer.latitude ? '—' : '-')}
                      {customer.longitude && customer.latitude ? (
                        <span>
                          {customer.country ? ' ' : ''}
                          <a
                            href={`https://www.google.com/maps?q=${customer.latitude},${customer.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary"
                          >
                            (View on map)
                          </a>
                        </span>
                      ) : null}
                    </dd>

                    <dt className="col-sm-4">Type:</dt>
                    <dd className="col-sm-8">
                      <span
                        className="badge contact-type-badge"
                        style={{ backgroundColor: getTypeBadgeColor(customer.type), color: '#fff' }}
                      >
                        {getTypeText(customer.type)}
                      </span>
                    </dd>

                    <dt className="col-sm-4">Date:</dt>
                    <dd className="col-sm-8">{formatDate(customer.date)}</dd>
                  </dl>
                </div>
                <div className="col-md-6">
                  <dl className="row">
                    <dt className="col-sm-4">Website:</dt>
                    <dd className="col-sm-8">
                      {customer.website ? (
                        <a href={customer.website} target="_blank" rel="noopener noreferrer">
                          {customer.website}
                        </a>
                      ) : (
                        '-'
                      )}
                    </dd>

                    <dt className="col-sm-4">Tags:</dt>
                    <dd className="col-sm-8">
                      {customer.tags && customer.tags.length > 0 ? (
                        <div className="d-flex flex-wrap gap-1">
                          {customer.tags.map(tag => (
                              <span
                                key={tag.id}
                                className="badge contact-type-badge"
                                style={{ backgroundColor: '#6b7280', color: '#fff' }}
                              >
                                {tag.name}
                              </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted">No tags</span>
                      )}
                    </dd>
                  </dl>
                </div>
              </div>
              <div className="mt-3">
                                {customer.description ? (
                  <div className="markdown-content">
                    <ReactMarkdown>{customer.description}</ReactMarkdown>
                  </div>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-12"></div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="card customer-details-card">
            <div className="card-header d-flex justify-content-between align-items-center gap-2 flex-wrap">
              <h5 className="mb-0">Contact Persons ({persons.length})</h5>
              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  aria-expanded={managingPersons}
                  aria-controls="customer-person-management"
                  onClick={() => setManagingPersons(value => !value)}
                  disabled={deletingPersonId !== null}
                >
                  {managingPersons ? 'Done' : 'Manage assignments'}
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(addRedirectToUrl(`/persons/new?customerId=${customer.id}`, location.pathname + location.search))}
                  disabled={deletingPersonId !== null}
                >
                  Create person
                </button>
              </div>
            </div>
            <div className="card-body projects-compact-body">
              {personsError && <div className="alert alert-danger" role="alert">{personsError}</div>}
              {managingPersons && (
                <div id="customer-person-management" className="mb-3">
                  <CustomerPersons
                    key={`${customer.id}-${personManagementVersion}`}
                    customerId={customer.id}
                    isEditing
                    onPersonsChange={refreshContactPersons}
                  />
                </div>
              )}
              {persons.length > 0 ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="text-start">Name</th>
                        <th className="text-start">Contact Type</th>
                        <th className="text-start">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPersons.map(person => (
                        <tr key={person.id}>
                          <td>
                            <div className="fw-bold">
                              {person.firstName} {person.lastName}
                            </div>
                            {(() => {
                              const cp = person.customers?.find(c => c.customerId === customer.id && !c.isFormer);
                              const role = cp?.jobTitle || cp?.department;
                              const social = person.socialLinks?.[0]?.url;

                              return (
                                <>
                                  {person.middleName && (
                                    <div className="text-muted small">{person.middleName}</div>
                                  )}
                                  {role && (
                                    <div className="text-muted small">{role}</div>
                                  )}
                                  {social && (
                                    <div className="small">
                                      <a href={social} target="_blank" rel="noreferrer" className="text-decoration-underline">
                                        {social}
                                      </a>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </td>
                          <td>{getContactTypeBadge(person.contactType)}</td>
                          <td>
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => navigate(`/persons/${person.id}`)}
                            >
                              View Details
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm ms-2"
                              onClick={() => navigate(addRedirectToUrl(`/persons/edit/${person.id}`, location.pathname + location.search))}
                              disabled={deletingPersonId !== null}
                            >
                              Edit person
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm ms-2"
                              onClick={() => handleDeletePerson(person)}
                              disabled={deletingPersonId !== null}
                            >
                              {deletingPersonId === person.id ? 'Deleting...' : 'Delete person'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No contact persons found for this customer.</p>
              )}
              {personTotalItems > 0 && (
                <Pagination
                  currentPage={personPage}
                  totalItems={personTotalItems}
                  itemsPerPage={personPageSize}
                  onPageChange={goToPersonPage}
                  onPageSizeChange={setPersonPageSize}
                  pageSizeOptions={[6, 12, 24]}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="card customer-details-card">
            <div className="card-header">
              <h5 className="mb-0">Projects ({customer.projects.length})</h5>
            </div>
            <div className="projects-compact-body card-body">
              {customer.projects.length > 0 ? (
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th className="text-start">Date</th>
                        <th className="text-start">Title</th>
                        <th className="text-start">Status</th>
                        <th className="text-start">Income</th>
                        <th className="text-start">Expense</th>
                        <th className="text-start">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProjects.map(project => {
                        const income = getProjectIncomeUsd(project.transactions ?? []);
                        const expense = getProjectExpenseUsd(project.transactions ?? []);
                        const statusTitle = getProjectStatusTitle(project.statusId);

                        return (
                          <tr key={project.id}>
                            <td>{formatDate(project.date)}</td>
                            <td>
                              <div className="d-flex flex-column">
                                <Link
                                  to={`/projects/${project.id}`}
                                  className="text-decoration-none fw-bold"
                                  style={{ color: 'inherit' }}
                                >
                                  {project.title}
                                </Link>
                                {project.po && (
                                  <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                                    {project.po}
                                  </small>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="badge contact-type-badge" style={{ backgroundColor: 'rgb(108, 117, 125)', color: '#fff' }}>
                                {statusTitle}
                              </span>
                            </td>
                            <td>
                              {income === 0 ? null : (
                                <span className="text-success fw-bold">
                                  {formatCurrency(income, 'USD')}
                                </span>
                              )}
                            </td>
                            <td>
                              {expense === 0 ? null : (
                                <span className="text-danger fw-bold">
                                  {formatCurrency(-expense, 'USD')}
                                </span>
                              )}
                            </td>
                            <td>{project.description || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {projectTotalItems > 0 && (
                    <Pagination
                      currentPage={projectPage}
                      totalItems={projectTotalItems}
                      itemsPerPage={projectPageSize}
                      onPageChange={goToProjectPage}
                      onPageSizeChange={setProjectPageSize}
                      pageSizeOptions={[5, 10, 25, 50]}
                    />
                  )}
                </div>
              ) : (
                <p className="text-muted mb-0">No projects found for this customer.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {unpaidTransactions.length > 0 && (
        <div className="row">
          <div className="col-12">
            <div className="card customer-details-card">
              <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <h5 className="mb-0">Unpaid Transactions</h5>
                <div className="customer-unpaid-table-total">
                  <span className="text-muted me-2">Total:</span>
                  <strong>{formatCurrency(totalUnpaidAmount, 'USD')}</strong>
                </div>
              </div>
              <div className="card-body">
                <TransactionsTable
                  transactions={paginatedTransactions}
                  highlightPendingStatus={true}
                  pendingStatusColor="#111111"
                  statusBadgeClassName="badge contact-type-badge"
                  showDirectionBadge={true}
                  directionBadgeColor="#4b5563"
                  sourceBadgeColor="#6b7280"
                />
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  pageSizeOptions={[5, 10, 25, 50]}
                />
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CustomerDetails;
